import * as React from 'react'

import {
  EXPOSURE_NONE,
  ResultRow,
  formatLastPracticed,
  formatMomentumScore,
  formatPercentage,
  formatTimeSpent,
  formatTrend,
  getRowMetrics,
  getSmartGroup,
  getSortableExposure,
  getSortableLastPracticed,
  getSortableMomentumScore,
  getSortablePercentage,
  getSortableSmartGroup,
  getSortableTime,
  getSortableTrend,
  smartGroupToClassName,
} from './helpers'

import { Tooltip, helpIcon, titleCaseMultiWordString, } from '../../Shared/index'

const helpIconImg = <img alt={helpIcon.alt} src={helpIcon.src} />

const headerWithTooltip = (label: string, tooltipText: string) => (
  <span className="text-and-tooltip-wrapper">
    <span>{label}</span>
    <Tooltip
      skipPositioning={true}
      tooltipText={tooltipText}
      tooltipTriggerText={helpIconImg}
    />
  </span>
)

// The final column renders Last Practiced for individual-student metrics and Number of Students Practiced otherwise.
export interface MetricColumnWidths {
  name: string;
  smartGroup: string;
  momentumScore: string;
  trend: string;
  exposure: string;
  questionsAnswered: string;
  replayRate: string;
  timeSpent: string;
  finalColumn: string;
}

// Aggregate-row tooltip copy differs slightly per report ("the students in
// this grade" vs "the students who practiced this standard"), so each report
// supplies its own phrasings; the individual-student copy is shared.
export interface AggregateMetricTooltips {
  momentumScore: string;
  trend: string;
  exposure: string;
  questionsAnswered: string;
  replayRate: string;
  timeSpent: string;
  numberOfStudents: string;
}

interface MetricHeadersConfig {
  widths: MetricColumnWidths;
  entityLabel: { singular: string; plural: string };
  goal: number;
  individualMetrics: boolean;
  aggregateTooltips: AggregateMetricTooltips;
}

export const buildNameHeader = ({ widths, entityLabel }: Pick<MetricHeadersConfig, 'widths' | 'entityLabel'>) => ({
  width: widths.name,
  ariaLabel: titleCaseMultiWordString(entityLabel.singular),
  name: titleCaseMultiWordString(entityLabel.singular),
  attribute: 'name',
  sortAttribute: 'nameForSort',
  isSortable: true,
  noTooltip: true,
})

export const buildMetricHeaders = ({ widths, entityLabel, goal, individualMetrics, aggregateTooltips }: MetricHeadersConfig) => {
  const metricPrefix = individualMetrics ? '' : 'Avg '
  const prefixed = (label: string) => `${metricPrefix}${label}`.trim()

  return [
    {
      width: widths.smartGroup,
      ariaLabel: 'Smart Group',
      name: headerWithTooltip('Smart Group', `Dynamic groups that compare each ${entityLabel.singular}'s ${individualMetrics ? '' : 'average '}Momentum Score against the goal of ${goal}, while accounting for ${individualMetrics ? '' : 'average '}exposure.`),
      attribute: 'smartGroup',
      sortAttribute: 'smartGroupForSort',
      isSortable: true,
    },
    {
      width: widths.momentumScore,
      ariaLabel: prefixed('Momentum Score'),
      name: headerWithTooltip(prefixed('Momentum Score'), individualMetrics
        ? `A growth-focused score that reflects this student's recent improvements and consistent effort.`
        : aggregateTooltips.momentumScore),
      attribute: 'avgMomentumScore',
      sortAttribute: 'avgMomentumScoreForSort',
      isSortable: true,
      rowSectionClassName: 'momentum-score tooltip-section',
    },
    {
      width: widths.trend,
      ariaLabel: prefixed('Momentum Score Trend'),
      name: headerWithTooltip(prefixed('Momentum Score Trend'), individualMetrics
        ? `Recent changes in this student's Momentum Score, revealing their performance trajectory.`
        : aggregateTooltips.trend),
      attribute: 'avgTrend',
      sortAttribute: 'avgTrendForSort',
      isSortable: true,
      rowSectionClassName: 'momentum-score-trend',
    },
    {
      width: widths.exposure,
      ariaLabel: prefixed('Exposure'),
      name: headerWithTooltip(prefixed('Exposure'), individualMetrics
        ? `A measure of practice this student has completed. Higher exposure increases the reliability of the Momentum Score.`
        : aggregateTooltips.exposure),
      attribute: 'avgExposure',
      sortAttribute: 'avgExposureForSort',
      isSortable: true,
    },
    {
      width: widths.questionsAnswered,
      ariaLabel: prefixed('Questions Answered'),
      name: headerWithTooltip(prefixed('Questions Answered'), individualMetrics
        ? `The total number of questions this student has answered.`
        : aggregateTooltips.questionsAnswered),
      attribute: 'avgQuestionsAnswered',
      sortAttribute: 'avgQuestionsAnsweredForSort',
      isSortable: true,
    },
    {
      width: widths.replayRate,
      ariaLabel: prefixed('Replay Rate'),
      name: headerWithTooltip(prefixed('Replay Rate'), individualMetrics
        ? `How often this student revisits questions. A high rate can signify both persistence and potential areas of difficulty.`
        : aggregateTooltips.replayRate),
      attribute: 'avgReplayRate',
      sortAttribute: 'avgReplayRateForSort',
      isSortable: true,
    },
    {
      width: widths.timeSpent,
      ariaLabel: prefixed('Time Spent'),
      name: headerWithTooltip(prefixed('Time Spent'), individualMetrics
        ? `The total time this student has spent answering questions.`
        : aggregateTooltips.timeSpent),
      attribute: 'avgTimeSpent',
      sortAttribute: 'avgTimeSpentForSort',
      isSortable: true,
    },
    individualMetrics
      ? {
        width: widths.finalColumn,
        ariaLabel: 'Last Practiced',
        name: headerWithTooltip('Last Practiced', 'The most recent date this student answered a question.'),
        attribute: 'lastPracticed',
        sortAttribute: 'lastPracticedForSort',
        isSortable: true,
      }
      : {
        width: widths.finalColumn,
        ariaLabel: 'Number of Students Practiced',
        name: headerWithTooltip('Number of Students Practiced', aggregateTooltips.numberOfStudents),
        attribute: 'numberOfStudents',
        sortAttribute: 'numberOfStudentsForSort',
        isSortable: true,
      },
  ]
}

export const buildMetricRowCells = (row: ResultRow, goal: number, nameForSort: string) => {
  const { last_practiced, number_of_students, } = row
  const { momentumScore, trend, exposure, questionsAnswered, replayRate, timeSpent, } = getRowMetrics(row)

  const smartGroup = getSmartGroup(exposure, momentumScore, questionsAnswered, goal)
  const exposureDisplay = questionsAnswered === 0 ? EXPOSURE_NONE : exposure

  return {
    nameForSort,
    smartGroup: <span className={smartGroupToClassName[smartGroup]}>{smartGroup}</span>,
    smartGroupForSort: getSortableSmartGroup(smartGroup, momentumScore, nameForSort),
    avgMomentumScore: formatMomentumScore(momentumScore, exposureDisplay),
    avgMomentumScoreForSort: getSortableMomentumScore(momentumScore),
    avgTrend: formatTrend(trend, questionsAnswered),
    avgTrendForSort: getSortableTrend(trend, questionsAnswered),
    avgExposure: exposureDisplay,
    avgExposureForSort: getSortableExposure(exposureDisplay),
    avgQuestionsAnswered: questionsAnswered || '—',
    avgQuestionsAnsweredForSort: questionsAnswered ?? -Infinity,
    avgReplayRate: formatPercentage(replayRate, questionsAnswered),
    avgReplayRateForSort: getSortablePercentage(replayRate, questionsAnswered),
    avgTimeSpent: formatTimeSpent(timeSpent),
    avgTimeSpentForSort: getSortableTime(timeSpent),
    lastPracticed: formatLastPracticed(last_practiced),
    lastPracticedForSort: getSortableLastPracticed(last_practiced),
    numberOfStudents: number_of_students ?? '—',
    numberOfStudentsForSort: number_of_students ?? -Infinity,
  }
}
